import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { AuthStep, AdminUser, AuthorizedApp, ActiveApp } from '../types';

/* Active workspace persistence. Per `specs/app-picker.md` §3A the FE stores `{id, name}` so the
 * header switcher trigger can render the workspace name on first paint without waiting for `GET /apps`. */
const ACTIVE_APP_STORAGE_KEY = 'corepilot.activeApp';

const COREPILOT_APP_TYPE = 'corePilot';

function readStoredActiveApp(): ActiveApp | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ACTIVE_APP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ActiveApp>;
    if (typeof parsed?.id !== 'string') return null;
    return { id: parsed.id, name: typeof parsed.name === 'string' ? parsed.name : null };
  } catch {
    return null;
  }
}

function writeStoredActiveApp(active: ActiveApp | null): void {
  if (typeof window === 'undefined') return;
  if (active) window.localStorage.setItem(ACTIVE_APP_STORAGE_KEY, JSON.stringify(active));
  else window.localStorage.removeItem(ACTIVE_APP_STORAGE_KEY);
}

/* Decide the active workspace at session start. Per `specs/app-picker.md` §4:
 *  - A previously-stored choice always wins — including for super admins who may have picked an app
 *    they're not directly bound to (the BE returns 403 on the next request if the choice is invalid,
 *    and the user can re-pick). This means a refresh on any authenticated page restores the user's
 *    last workspace instead of bouncing them back to the picker.
 *  - 0 apps → null (sign-out state)
 *  - 1 app & non-super → auto-pick that app, regardless of appType (matches the dashboard pattern;
 *    multi-tenant accounts surface the picker, single-tenant accounts go straight to home)
 *  - else (multi-app or super, no stored choice) → null; the gate sends the user to `/workspaces`
 *
 * When auto-picking among many bindings we prefer corePilot ones, since this app talks to corePilot
 * endpoints — but if the user only has non-corePilot bindings, we still return one of those so the
 * auth never hard-fails on a binding-shape mismatch. */
function pickInitialActiveApp(apps: AuthorizedApp[], isSuperAdmin: boolean): ActiveApp | null {
  const stored = readStoredActiveApp();
  if (stored) return stored;

  if (apps.length === 0) return null;

  const corePilot = apps.filter((a) => a.appType === COREPILOT_APP_TYPE);
  const candidates = corePilot.length > 0 ? corePilot : apps;

  if (candidates.length === 1 && !isSuperAdmin) {
    const only = candidates[0];
    if (only) return { id: only.app, name: null };
  }

  return null;
}

type AuthStore = {
  step: AuthStep;
  pending2FAToken: string | null;
  userEmail: string | null;
  user: AdminUser | null;
  apps: AuthorizedApp[];
  /** The workspace the user is currently acting in. The id flows out as `gamix-app-id` on every API call. */
  activeApp: ActiveApp | null;
  scopes: string[];
  featureFlags: string[];
  roleKey: string | null;
  /* True once `setPermissions` has populated scopes/flags/role for the current session+app.
   * Login only sets user+apps; scopes are loaded by a separate /me call (see useSessionBootstrap).
   * AuthGuard blocks gated pages until this flips, so the Conversations <Gate> never renders
   * against an empty scope list. Reset on logout and on workspace switch (scopes are per-app). */
  permissionsLoaded: boolean;

  startLogin: (email: string) => void;
  require2fa: (token: string) => void;
  authenticate: (user: AdminUser, apps: AuthorizedApp[]) => void;
  setActiveApp: (app: ActiveApp) => void;
  setPermissions: (scopes: string[], flags: string[], roleKey: string | null) => void;
  reset: () => void;
};

export const useAuthStore = create<AuthStore>((set) => ({
  step: 'credentials',
  pending2FAToken: null,
  userEmail: null,
  user: null,
  apps: [],
  activeApp: null,
  scopes: [],
  featureFlags: [],
  roleKey: null,
  permissionsLoaded: false,

  startLogin: (email) => set({ userEmail: email }),

  require2fa: (token) => set({ step: '2fa', pending2FAToken: token }),

  authenticate: (user, apps) => {
    const isSuperAdmin = !!user.role;
    const activeApp = pickInitialActiveApp(apps, isSuperAdmin);
    writeStoredActiveApp(activeApp);
    set({ step: 'authenticated', user, apps, activeApp, pending2FAToken: null });
  },

  setActiveApp: (app) => {
    /* No-op when the picker re-emits the current workspace — avoids needlessly clearing the
     * scope cache and re-firing the bootstrap query for what looks like a switch but isn't. */
    if (useAuthStore.getState().activeApp?.id === app.id) return;
    writeStoredActiveApp(app);
    /* Scopes are per-app — clear them and re-fetch. AuthGuard sees `permissionsLoaded: false`
     * and re-runs the bootstrap query against the new active app. */
    set({
      activeApp: app, scopes: [], featureFlags: [], roleKey: null, permissionsLoaded: false,
    });
  },

  setPermissions: (scopes, flags, roleKey) =>
    set({
      scopes, featureFlags: flags, roleKey, permissionsLoaded: true,
    }),

  reset: () => {
    writeStoredActiveApp(null);
    set({
      step: 'credentials',
      pending2FAToken: null,
      userEmail: null,
      user: null,
      apps: [],
      activeApp: null,
      scopes: [],
      featureFlags: [],
      roleKey: null,
      permissionsLoaded: false,
    });
  },
}));

export type AuthState = {
  step: AuthStep;
  email: string | null;
  user: AdminUser | null;
  apps: AuthorizedApp[];
  activeApp: ActiveApp | null;
  isSuperAdmin: boolean;
};

export function useAuthState(): AuthState {
  return useAuthStore(
    useShallow((s) => ({
      step: s.step,
      email: s.userEmail,
      user: s.user,
      apps: s.apps,
      activeApp: s.activeApp,
      isSuperAdmin: !!s.user?.role,
    })),
  );
}

export function useActiveApp(): ActiveApp | null {
  return useAuthStore((s) => s.activeApp);
}

export function useActiveAppId(): string | null {
  return useAuthStore((s) => s.activeApp?.id ?? null);
}

export function useSetActiveApp(): (app: ActiveApp) => void {
  return useAuthStore((s) => s.setActiveApp);
}

export function useIsSuperAdmin(): boolean {
  return useAuthStore((s) => !!s.user?.role);
}

/**
 * `true` for workspace admins — Super Admin (cross-tenant) or `corePilotAdmin`
 * (in-workspace admin). Sales Manager and Sales Rep return `false`. Used by
 * surfaces that are intentionally limited to admins (e.g. the Captain canvas
 * toggle in the AppShell header). The BE re-checks every protected action,
 * so this is a UX courtesy, not a security boundary.
 */
export function useIsAdmin(): boolean {
  return useAuthStore((s) => !!s.user?.role || s.roleKey === 'corePilotAdmin');
}

export function useResetAuth(): () => void {
  return useAuthStore((s) => s.reset);
}

export function useScopes(): string[] {
  return useAuthStore((s) => s.scopes);
}

/**
 * Returns `true` when the current user holds the given scope. Lets components hide actions
 * the user can't perform — note the BE re-checks every request, so this is a UX courtesy
 * (don't show a button only to 403 on click), not a security boundary.
 */
export function useHasScope(scope: string): boolean {
  return useAuthStore((s) => s.scopes.includes(scope));
}

export function useFeatureFlags(): string[] {
  return useAuthStore((s) => s.featureFlags);
}

export function usePermissionsLoaded(): boolean {
  return useAuthStore((s) => s.permissionsLoaded);
}
